import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, Clock, User, Building2, Mail, Phone, ArrowRight, CheckCircle, Shield, Target, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";

export default function ConsultationBooking({
  calendlyUrl = "",
  verticalId = "",
  verticalName = "",
  buttonText = "Schedule Free Consultation",
  buttonClassName = "",
  buttonVariant = "default",
  buttonSize = "default",
}) {
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "",
    problem: "", willing_to_invest: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.problem) {
      toast.error("Please fill in your name, email, and current challenge");
      return;
    }
    setSaving(true);
    try {
      await submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        vertical_interest: verticalName,
        problem_interest: `Consultation Request - ${verticalName}`,
        message: `Problem: ${formData.problem}\nWilling to invest time: ${formData.willing_to_invest}`,
        source: "consultation_booking"
      });
      setSubmitted(true);
      toast.success("Consultation request submitted successfully!");
    } catch {
      toast.error("Failed to submit. Please try again.");
    }
    setSaving(false);
  };

  const reset = () => {
    setSubmitted(false);
    setFormData({ name: "", email: "", phone: "", company: "", problem: "", willing_to_invest: "" });
    setShowModal(false);
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName || "bg-[#0066CC] hover:bg-[#0052A3] gap-2"}
        data-testid="consultation-booking-btn"
      >
        <Calendar className="h-4 w-4" />
        {buttonText}
      </Button>

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="max-w-[920px] p-0 overflow-hidden" data-testid="consultation-modal">
          {submitted ? (
            <div className="text-center py-14 px-8">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Request Received!</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Our team will review your consultation request and contact you within 24 hours to schedule a session.
              </p>
              <Button onClick={reset} className="bg-[#0066CC] hover:bg-[#0052A3]">Close</Button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row">
              {/* Left: Form */}
              <div className="flex-1 p-6 md:p-8">
                <div className="flex items-center gap-2.5 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                    <Target className="h-5 w-5 text-[#0066CC]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 leading-tight">Request a Free Consultation</h2>
                    <p className="text-xs text-slate-400">Takes less than 2 minutes</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name" className="pl-10 h-10 text-sm bg-slate-50/50 border-slate-200 focus:bg-white" required data-testid="consult-name" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="you@company.com" className="pl-10 h-10 text-sm bg-slate-50/50 border-slate-200 focus:bg-white" required data-testid="consult-email" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210" className="pl-10 h-10 text-sm bg-slate-50/50 border-slate-200 focus:bg-white" data-testid="consult-phone" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Company</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="Company name" className="pl-10 h-10 text-sm bg-slate-50/50 border-slate-200 focus:bg-white" data-testid="consult-company" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">What is your current challenge or problem? *</Label>
                    <Textarea value={formData.problem} onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                      placeholder="Describe the operational challenge you want to address..."
                      rows={3} className="text-sm bg-slate-50/50 border-slate-200 focus:bg-white resize-none" required data-testid="consult-problem" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-slate-600">Are you willing to invest 2 hours for an in-depth consultation?</Label>
                    <div className="flex gap-3">
                      {["Yes, absolutely", "Need more info first"].map((opt) => (
                        <button key={opt} type="button"
                          onClick={() => setFormData({ ...formData, willing_to_invest: opt })}
                          className={`px-4 py-2 rounded-lg text-xs font-medium border transition-all ${
                            formData.willing_to_invest === opt
                              ? "bg-[#0066CC] text-white border-[#0066CC] shadow-sm"
                              : "bg-white text-slate-600 border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC]"
                          }`} data-testid={`consult-invest-${opt.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" disabled={saving}
                    className="w-full h-11 bg-[#0066CC] hover:bg-[#0052A3] text-sm font-semibold shadow-md shadow-[#0066CC]/15 hover:shadow-lg hover:shadow-[#0066CC]/20 transition-all"
                    data-testid="consult-submit">
                    {saving ? "Submitting..." : "Request Consultation"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </div>

              {/* Right: Value Props */}
              <div className="md:w-[300px] bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100 p-6 md:p-7 flex flex-col gap-5">
                <div className="bg-white rounded-xl p-5 border border-blue-100 shadow-sm">
                  <h4 className="font-bold text-[#0066CC] text-sm mb-4">What You Get</h4>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Clock className="w-3.5 h-3.5 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">2-Hour Deep Dive</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Dedicated session with our senior consultants</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Target className="w-3.5 h-3.5 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Gap Analysis</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Identify digital transformation gaps in your operations</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Lightbulb className="w-3.5 h-3.5 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Actionable Insights</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Receive a custom roadmap for your business</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Shield className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-800">No Sales Pitch Guarantee</p>
                      <p className="text-xs text-emerald-600 mt-1 leading-relaxed">
                        This consultation is purely to understand your digital transformation gaps.
                        No products or services will be pitched during this session.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
